import {
  Backdrop,
  Box,
  Checkbox,
  Link,
  styled,
  Typography,
} from '@mui/material';

import { Icon } from '../Icon';

const INPUT_BG = 'rgba(9, 13, 17, 0.55)';

export const Wrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 560,
  borderRadius: 32,
  background: '#0E1419',
  padding: theme.spacing(5),
  outline: 'none',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: 'none',
  boxShadow: '0 32px 64px rgba(0, 0, 0, 0.45)',
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'calc(100% - 32px)',
    padding: theme.spacing(3.5),
    borderRadius: 24,
  },
}));

export const Form = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
}));

export const BlurredBackdrop = styled(Backdrop)(() => ({
  backgroundColor: 'rgba(0, 0, 0, 0.80)',
  backdropFilter: 'blur(4px)',
}));

export const CloseButton = styled('button')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  right: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  backgroundColor: theme.palette.color.button.secondaryDefault,
  color: theme.palette.color.fg.primary,
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.color.button.secondaryHover,
  },

  [theme.breakpoints.down('sm')]: {
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

export const CloseIcon = styled(Icon)(() => ({
  width: 20,
  height: 20,
}));

export const TextBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(2),
  width: '100%',
  paddingTop: theme.spacing(1),
}));

export const Title = styled(Typography)(({ theme }) => ({
  fontSize: 40,
  fontWeight: 700,
  lineHeight: 1.1,
  letterSpacing: '-0.03em',
  color: '#ffffff',
  maxWidth: 420,
  [theme.breakpoints.down('sm')]: {
    fontSize: 36,
  },
}));

export const Description = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  maxWidth: 480,
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
    lineHeight: 1.5,
  },
}));

export const FormFields = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
}));

export const EmailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export const EmailFieldWrapper = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
  position: 'relative',
}));

export const EmailInput = styled('input')(({ theme, error }) => ({
  width: '100%',
  height: 52,
  padding: '0 20px 0 48px',
  fontSize: 16,
  lineHeight: '24px',
  color: '#ffffff',
  borderRadius: 999,
  border: '1px solid',
  borderColor: error
    ? theme.palette.color.red['07']
    : 'rgba(255, 255, 255, 0.12)',
  background: INPUT_BG,
  outline: 'none',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  '&:focus': {
    borderColor: 'rgba(255, 255, 255, 0.28)',
    background: 'rgba(9, 13, 17, 0.72)',
  },
  '&::placeholder': {
    color: '#758692',
  },
  '&:-webkit-autofill': {
    WebkitTextFillColor: '#ffffff !important',
    transition: 'background-color 9999s ease-in-out 0s',
  },
}));

export const EmailIcon = styled(Icon)(() => ({
  position: 'absolute',
  left: 20,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 18,
  height: 18,
  color: '#ffffff',
  opacity: 0.9,
  pointerEvents: 'none',
}));

export const InputError = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  left: 20,
  bottom: -22,
  fontSize: 12,
  color: theme.palette.global.input.error.text,
}));

export const SubscribeButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  minWidth: 140,
  height: 52,
  padding: '0 28px',
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1,
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  color: '#121b22',
  backgroundColor: '#e2eaf0',
  transition: 'background-color 0.2s ease, transform 0.15s ease',
  '&:hover:not(:disabled)': {
    backgroundColor: '#f0f5f9',
  },
  '&:active:not(:disabled)': {
    transform: 'scale(0.98)',
  },
  '&:disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 0,
  },
}));

export const ConsentRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  width: '100%',
  textAlign: 'left',
}));

export const ConsentCheckbox = styled(Checkbox)(() => ({
  padding: 0,
  marginTop: 1,
  color: 'rgba(255, 255, 255, 0.35)',
  '&.Mui-checked': {
    color: '#e2eaf0',
  },
  '& .MuiSvgIcon-root': {
    width: 18,
    height: 18,
    borderRadius: 4,
  },
}));

export const ConsentText = styled(Typography)(() => ({
  fontSize: 13,
  lineHeight: 1.5,
  color: '#acb8c1',
}));

export const PrivacyLink = styled(Link)(() => ({
  color: 'inherit',
  fontWeight: 500,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  cursor: 'pointer',
  '&:hover': {
    color: '#ffffff',
  },
}));

export const ConsentError = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.global.input.error.text,
  width: '100%',
  textAlign: 'left',
}));

export const SuccessActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  marginTop: theme.spacing(0.5),
}));

export const OkButton = styled(SubscribeButton)(() => ({}));
